import { useTranslation } from "react-i18next";
import { Dispatch, SetStateAction, useMemo } from "react";

import { Box, TextField, MenuItem, Autocomplete, CircularProgress } from "@mui/material";
import { Person2Outlined } from "@mui/icons-material";

import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { faList } from "@fortawesome/pro-regular-svg-icons";

import { Icon, Modal } from "@/components";

import { ERROR_TYPE, translateError } from "@/utils";

import { useGetAllTagsQuery } from "@/pages/Private/redux/to-do/to-do.api";

import { useGetAssigneesQuery } from "@/pages/Private/redux/tasks-and-subtasks/tasks-and-subtasks.api";

import { ToDoStatus } from "../enum/ToDoStatus.enum";
import { ToDoPriority } from "../enum/ToDoPriority.enum";
import { CreateTaskInfo, CreateTaskInfoSchema } from "../schema/tasks-and-subtasks";
import { useToDoSocket } from "../../ToDo/ToDoList/providers/SocketContext";

type TagOption = { tagId: number; tagName: string };

interface CreateNewTaskModalProps {
	isOpen: boolean;
	setIsOpen: Dispatch<SetStateAction<boolean>>;
}

const thinErrorBorderSx = {
	"& .Mui-error fieldset.MuiOutlinedInput-notchedOutline": {
		borderWidth: "1px",
	},
};

export default function CreateNewTaskModal({ setIsOpen, isOpen }: CreateNewTaskModalProps) {
	const { t } = useTranslation();
	const ts = (key: string, options = {}) => t(`createTask.${key}`, options);

	const defaultValues: CreateTaskInfo = useMemo(
		() => ({
			title: "",
			description: "",
			assigneeId: null,
			priority: ToDoPriority.MEDIUM,
			tagId: undefined,
			complexityEstimate: 1,
		}),
		[]
	);

	const {
		control,
		handleSubmit,
		formState: { errors },
		reset,
	} = useForm<CreateTaskInfo>({
		defaultValues,
		resolver: zodResolver(CreateTaskInfoSchema),
	});

	const { createTask } = useToDoSocket();

	const { data: tags, isLoading: isTagsLoading } = useGetAllTagsQuery();

	const tagOptions: TagOption[] =
		tags
			?.filter((t): t is { id: number; name: string } => !!t.id)
			.map((t) => ({ tagId: t.id, tagName: t.name })) ?? [];

	const { data: users, isLoading: isUsersLoading } = useGetAssigneesQuery();

	const unassignedOption = {
		firstName: t("createTask.unassigned"),
		lastName: "",
		assigneeId: null as unknown as number,
	};

	const assigneeOptions = [
		unassignedOption,
		...(users?.map((user) => ({
			firstName: user.firstName,
			lastName: user.lastName,
			assigneeId: +user.id,
		})) ?? []),
	];

	const onSubmit = async (values: CreateTaskInfo) => {
		try {
			await createTask({ ...values, status: values.status ?? ToDoStatus.TO_DO });
			setIsOpen(false);
			reset(defaultValues);
		} catch (err) {
			console.error(err);
		}
	};

	const isInitialLoading = isTagsLoading || isUsersLoading;

	return (
		<Modal
			aboveHeader={<Icon icon={faList} />}
			dialogContentClassName="!px-4 max-h-[80vh] overflow-auto mr-1 custom-scrollbar"
			handleClose={() => setIsOpen(false)}
			handleSave={handleSubmit(onSubmit)}
			isOpened={isOpen}
			saveButtonContainerClassName="!w-full"
			size="sm"
			submitButtonText={ts("save")}
			title={ts("mainTitle")}
		>
			{isInitialLoading ? (
				<div className="w-full flex justify-center py-6">
					<CircularProgress size={24} />
				</div>
			) : (
				<Box className="flex flex-col gap-3 w-full" sx={thinErrorBorderSx}>
					<Controller
						control={control}
						name="title"
						render={({ field }) => (
							<TextField
								{...field}
								fullWidth
								error={!!errors?.title}
								helperText={
									!!errors?.title?.message
										? translateError[errors?.title?.message as unknown as ERROR_TYPE]
										: errors?.title?.message
								}
								label={ts("title")}
								size="small"
							/>
						)}
					/>

					<Controller
						control={control}
						name="description"
						render={({ field }) => (
							<TextField
								{...field}
								fullWidth
								multiline
								label={ts("description")}
								maxRows={6}
								minRows={6}
								size="small"
							/>
						)}
					/>

					<Controller
						control={control}
						name="assigneeId"
						render={({ field }) => (
							<Autocomplete
								disableClearable
								defaultValue={unassignedOption}
								getOptionLabel={(o) => `${o.firstName ?? ""} ${o.lastName ?? ""}`.trim()}
								isOptionEqualToValue={(a, b) => a.assigneeId === b.assigneeId}
								options={assigneeOptions}
								renderInput={(params) => (
									<TextField
										{...params}
										InputProps={{
											...params.InputProps,
											startAdornment: (
												<>
													<Person2Outlined
														sx={{ color: "action.active", mr: 0.5, my: 0.5, fontSize: 18 }}
													/>
													{params.InputProps.startAdornment}
												</>
											),
										}}
										error={!!errors?.assigneeId}
										helperText={
											!!errors?.assigneeId?.message
												? translateError[errors?.assigneeId?.message as unknown as ERROR_TYPE]
												: errors?.assigneeId?.message
										}
										label={ts("assignee")}
									/>
								)}
								size="small"
								value={
									assigneeOptions.find((o) => o?.assigneeId === field.value) ?? unassignedOption
								}
								onChange={(_, v) => field.onChange(v?.assigneeId ?? null)}
							/>
						)}
					/>

					<Box className="grid grid-cols-2 gap-3 w-full">
						{/* <Controller
							control={control}
							name="priority"
							render={({ field }) => (
								<TextField
									fullWidth
									select
									label={ts("priority")}
									size="small"
									value={field.value}
									onChange={(e) => field.onChange(e.target.value)}
								>
									<MenuItem value={ToDoPriority.LOW}>{ts("low")}</MenuItem>
									<MenuItem value={ToDoPriority.MEDIUM}>{ts("medium")}</MenuItem>
									<MenuItem value={ToDoPriority.HIGH}>{ts("high")}</MenuItem>
								</TextField>
							)}
						/> */}
						{/* <Controller
							control={control}
							name="complexityEstimate"
							render={({ field }) => (
								<TextField
									fullWidth
									select
									label={ts("complexity")}
									size="small"
									value={field.value}
									onChange={(e) => field.onChange(Number(e.target.value))}
								>
									<MenuItem value={1}>{ts("easy")}</MenuItem>
									<MenuItem value={2}>{ts("normal")}</MenuItem>
									<MenuItem value={3}>{ts("hard")}</MenuItem>
									<MenuItem value={4}>{ts("very-hard")}</MenuItem>
									<MenuItem value={5}>{ts("expert")}</MenuItem>
								</TextField>
							)}
						/> */}
					</Box>

					<Controller
						control={control}
						name="tagId"
						render={({ field }) => (
							<Autocomplete<TagOption>
								getOptionLabel={(o) => o.tagName}
								isOptionEqualToValue={(a, b) => a.tagId === b.tagId}
								options={tagOptions}
								renderInput={(params) => <TextField {...params} label={ts("tags")} />}
								size="small"
								value={tagOptions.find((o) => o.tagId === field.value) ?? null}
								onChange={(_, selected) => field.onChange(selected?.tagId ?? undefined)}
							/>
						)}
					/>
				</Box>
			)}
		</Modal>
	);
}
